/**
 * Reading the crew's framing nailer for an order (ADR 0287).
 *
 * Its own module rather than a line in queries.ts: the screens that generate
 * orders are tested with queries.ts replaced wholesale, and a crew with no gun
 * is what those tests mean.
 */

import {
  crewsForNails,
  nailerForCrews,
  type FramingNailer,
} from "@roof-hawk/core";

import { supabase, unwrap } from "./supabase";

/**
 * The framing nailer an order's nails have to fit, and the catalogue told which
 * nail fits which gun (ADR 0287).
 *
 * The crew is the one on the estimate's work orders for the order's trade;
 * before there is a work order, it is the crew the template will put on it.
 *
 * `crews(*)` rather than a column list, so a database the migration has not
 * reached reads every crew as having no gun, and nothing is swapped. The
 * products are asked for their gun only once a crew has one, which proves the
 * same migration has run.
 */
export async function fetchFramingNails(input: {
  estimateId?: string | null;
  trade?: string | null;
  fallbackCrewId?: string | null;
}): Promise<{
  nailer: FramingNailer | null;
  tag: <P extends { id: string }>(
    products: readonly P[],
  ) => (P & { framing_nailer: string | null })[];
}> {
  type CrewGun = { framing_nailer?: string | null };
  const workOrders = input.estimateId
    ? unwrap<{ trades: string[] | null; crew: CrewGun | null }[]>(
        await supabase
          .from("work_orders")
          .select("trades, crew:crews(*)")
          .eq("estimate_id", input.estimateId),
      )
    : [];
  const fallbackCrew = input.fallbackCrewId
    ? (unwrap<CrewGun[]>(
        await supabase.from("crews").select("*").eq("id", input.fallbackCrewId),
      )[0] ?? null)
    : null;
  const nailer = nailerForCrews(
    crewsForNails({ trade: input.trade ?? null }, workOrders, fallbackCrew),
  );

  const guns = new Map<string, string>();
  if (nailer !== null) {
    for (const row of unwrap<{ id: string; framing_nailer: string }[]>(
      await supabase
        .from("products")
        .select("id,framing_nailer")
        .not("framing_nailer", "is", null),
    )) {
      guns.set(row.id, row.framing_nailer);
    }
  }
  return {
    nailer,
    tag: (products) =>
      products.map((p) => ({ ...p, framing_nailer: guns.get(p.id) ?? null })),
  };
}
